import { confirmLeave, setLeaveGuard } from "../leave-guard";
import {
  Check,
  ChevronLeft,
  Clock3,
  Copy,
  History,
  Link2,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  RotateCcw,
  Search,
  X,
} from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError, formatDate, formatDateTime } from "../api";
import { useAuth } from "../auth";
import DocEditor, { type DocEditorHandle } from "../components/DocEditor";
import { plainTextToMarkdown } from "../markdown-text";
import { calmHeadings, splitCover } from "../lesson-doc";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "../components/Ui";
import { useApi } from "../hooks";

type KbRow = { id: number; doc_code: string; title: string; version: string; applies_to: string; active: boolean; updated_at: string; is_empty?: boolean };
type KbDoc = KbRow & { full_text: string | null };
type KbVersion = { id: number; created_at: string; full_text: string | null; note: string | null; edited_by: string | null };

const AUDIENCE: Record<string, string> = {
  all: "All clients",
  franchise: "Franchise",
  legal: "Legal",
  junk_king: "Junk King",
  bugbros: "BugBros",
};
const audience = (v: string) => AUDIENCE[v] || v.replace(/_/g, " ");
const COLLAPSE_KEY = "nl-aos-kb-nav-collapsed";

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const area = document.createElement("textarea");
    area.value = text;
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    document.execCommand("copy");
    area.remove();
  }
}

export default function KnowledgeBase() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === "superadmin";
  const list = useApi<KbRow[]>("/api/kb");
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
  });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  // One list in code order. Documents for one kind of client carry a small tag instead of a group of their own,
  // so KB-006 sits between KB-005 and KB-008 and the count above the list is the whole library.
  const docs = useMemo(() => (list.data || []).filter((d) => d.active !== false), [list.data]);
  const q = query.trim().toLowerCase();
  const filtered = docs.filter((d) => `${d.doc_code} ${d.title} ${d.applies_to === "all" ? "" : audience(d.applies_to)}`.toLowerCase().includes(q));
  const navList = useRef<HTMLElement>(null);

  // Leaving a document with unsaved edits asks first, in-app and on reload.
  const go = (to: string) => {
    if (!confirmLeave()) return;
    setDirty(false);
    navigate(to);
  };
  // The same question covers the sidebar, client links and Cmd+K (leave-guard.ts).
  useEffect(() => {
    setLeaveGuard(dirty ? "You have unsaved changes. Leave this document and discard them?" : null);
    return () => setLeaveGuard(null);
  }, [dirty]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const current = code ? docs.find((d) => d.doc_code.toUpperCase() === code.toUpperCase()) : undefined;

  // The open document's row is always in view in the list (it can sit far down a 48-row list).
  // Only the list scrolls: the page stays where it is.
  useEffect(() => {
    const nav = navList.current;
    const row = nav?.querySelector<HTMLElement>(".kb-nav-item.active");
    if (!nav || !row) return;
    const box = nav.getBoundingClientRect();
    const r = row.getBoundingClientRect();
    if (r.top < box.top) nav.scrollTop -= box.top - r.top + 8;
    else if (r.bottom > box.bottom) nav.scrollTop += r.bottom - box.bottom + 8;
  }, [current?.doc_code, list.data]);

  return (
    <div className={["kb-shell", collapsed && "nav-collapsed", !code && "is-home"].filter(Boolean).join(" ")}>
      <aside className="kb-nav" aria-label="Knowledge documents">
        <div className="kb-nav-head">
          <label className="kb-search">
            <Search aria-hidden="true" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search documents…" aria-label="Search documents" />
          </label>
          <button type="button" className="icon-button kb-nav-hide" aria-label="Hide document list" title="Hide document list" onClick={() => setCollapsed(true)}>
            <PanelLeftClose aria-hidden="true" />
          </button>
        </div>
        {!!docs.length && (
          <p className="kb-nav-count" aria-live="polite">{q ? `${filtered.length} of ${docs.length} documents` : `${docs.length} documents`}</p>
        )}
        <nav className="kb-nav-list" ref={navList}>
          {list.loading && !list.data ? <p className="kb-nav-note">Loading…</p> : list.error ? (
            <p className="kb-nav-note">Could not load. <button type="button" className="text-button" onClick={list.reload}>Retry</button></p>
          ) : !filtered.length ? <p className="kb-nav-note">No matching documents.</p> : filtered.map((d) => {
            const tag = d.is_empty ? "No text yet" : d.applies_to !== "all" ? audience(d.applies_to) : null;
            return (
              <a
                key={d.doc_code}
                href={`/knowledge/${d.doc_code}`}
                className={current?.doc_code === d.doc_code ? "kb-nav-item active" : "kb-nav-item"}
                aria-current={current?.doc_code === d.doc_code ? "page" : undefined}
                onClick={(e) => { e.preventDefault(); go(`/knowledge/${d.doc_code}`); }}
                title={tag ? `${d.title} (${tag})` : d.title}
              >
                <span className="kb-code">{d.doc_code}</span>
                <span className="kb-nav-name">{d.title}</span>
                {tag && <span className="kb-nav-tag">{tag}</span>}
              </a>
            );
          })}
        </nav>
      </aside>

      <section className="kb-main">
        {collapsed && (
          <button type="button" className="icon-button kb-reopen" aria-label="Show document list" title="Show document list" onClick={() => setCollapsed(false)}>
            <PanelLeftOpen aria-hidden="true" />
          </button>
        )}
        {!code ? (
          <KbHome docs={docs} loading={list.loading && !list.data} onOpen={(c) => go(`/knowledge/${c}`)} />
        ) : (
          <KbDocument
            key={code}
            code={code}
            canEdit={canEdit}
            onDirtyChange={setDirty}
            onSaved={list.reload}
            onNavigate={go}
          />
        )}
      </section>
    </div>
  );
}

function KbHome({ docs, loading, onOpen }: { docs: KbRow[]; loading: boolean; onOpen: (code: string) => void }) {
  if (loading) return <LoadingState label="Loading knowledge" />;
  const recent = [...docs].sort((a, b) => b.updated_at.localeCompare(a.updated_at)).slice(0, 6);
  return (
    <div className="kb-page kb-home">
      {/* The list beside this counts the documents; the title uses the shared page header scale. */}
      <PageHeader title="Knowledge base" description="Agency standards and playbooks used by the analyst and the delivery team." />
      {!docs.length ? <EmptyState title="No knowledge documents" description="Published agency knowledge will appear here." /> : (
        <section className="kb-recent" aria-labelledby="kb-recent-title">
          <h2 id="kb-recent-title" className="kb-section-title"><Clock3 aria-hidden="true" /> Recently updated</h2>
          {/* A compact list with aligned columns: title, code, date. */}
          <ul className="kb-recent-list">
            {recent.map((d) => (
              <li key={d.doc_code}>
                <a href={`/knowledge/${d.doc_code}`} className="kb-recent-row" onClick={(e) => { e.preventDefault(); onOpen(d.doc_code); }}>
                  <span className="kb-recent-name">{d.title}</span>
                  <span className="kb-code">{d.doc_code}</span>
                  <span className="kb-recent-date">{formatDate(d.updated_at)}</span>
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** The first block of the document under the sticky bars, so switching between reading and editing keeps the reader's place. */
type Place = { key: string; nth: number; top: number; y: number };
const blockKey = (el: Element) => (el.textContent || "").replace(/\s+/g, " ").trim().slice(0, 120);
function readPlace(article: HTMLElement | null): Place | null {
  if (!article) return null;
  const barBottom = article.closest(".kb-doc")?.querySelector(".kb-topbar")?.getBoundingClientRect().bottom ?? 0;
  // Still at the top (the title is in view): nothing to keep.
  if (article.getBoundingClientRect().top >= barBottom) return null;
  const blocks = [...article.querySelectorAll<HTMLElement>(".doc-prose > *")];
  const at = blocks.find((el) => el.getBoundingClientRect().bottom > barBottom + 8);
  if (!at) return null;
  const key = blockKey(at);
  return { key, nth: blocks.slice(0, blocks.indexOf(at)).filter((el) => blockKey(el) === key).length, top: at.getBoundingClientRect().top, y: window.scrollY };
}
function restorePlace(article: HTMLElement | null, place: Place) {
  const same = [...(article?.querySelectorAll<HTMLElement>(".doc-prose > *") || [])].filter((el) => place.key && blockKey(el) === place.key);
  const el = same[place.nth] || same[0];
  if (el) window.scrollBy(0, el.getBoundingClientRect().top - place.top);
  else window.scrollTo(0, place.y);
}

function KbDocument({
  code,
  canEdit,
  onDirtyChange,
  onSaved,
  onNavigate,
}: {
  code: string;
  canEdit: boolean;
  onDirtyChange: (dirty: boolean) => void;
  onSaved: () => void;
  onNavigate: (to: string) => void;
}) {
  const doc = useApi<KbDoc>(`/api/kb/${encodeURIComponent(code)}`);
  const editorRef = useRef<DocEditorHandle>(null);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: "danger" | "success"; text: string } | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [copied, setCopied] = useState<"" | "link" | "text">("");
  const [editorKey, setEditorKey] = useState(0);
  // Text a restore loaded into the editor; null means "the saved text".
  const [draftSource, setDraftSource] = useState<string | null>(null);
  const article = useRef<HTMLElement>(null);
  const place = useRef<Place | null>(null);

  // Edit, Cancel and Save swap the reading view for the editor and back. The block
  // that was at the top of the screen stays there, instead of the page jumping.
  const switchEditing = useCallback((next: boolean) => {
    place.current = readPlace(article.current);
    setEditing(next);
  }, []);
  useLayoutEffect(() => {
    const kept = place.current;
    place.current = null;
    if (kept) restorePlace(article.current, kept);
  }, [editing]);

  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  useEffect(() => { if (doc.data) setTitle(doc.data.title); }, [doc.data]);

  const markDirty = useCallback(() => setDirty(true), []);

  const save = useCallback(async () => {
    if (!doc.data || !editorRef.current || saving) return;
    const trimmed = title.trim();
    if (!trimmed) { setMessage({ tone: "danger", text: "Give the document a title before saving." }); return; }
    setSaving(true);
    setMessage(null);
    try {
      // Trailing blank lines are editor residue, not content.
      const fullText = editorRef.current.getMarkdown().replace(/\s+$/, "") + "\n";
      const result = await api<{ title: string; version: string; updated_at: string }>(`/api/kb/${encodeURIComponent(doc.data.doc_code)}`, {
        method: "PUT",
        body: JSON.stringify({
          full_text: fullText,
          title: trimmed !== doc.data.title ? trimmed : undefined,
          base_updated_at: new Date(doc.data.updated_at).getTime(),
        }),
      });
      doc.setData({ ...doc.data, full_text: fullText, title: result.title, updated_at: result.updated_at });
      setDirty(false);
      switchEditing(false);
      setDraftSource(null);
      setMessage({ tone: "success", text: "Saved. The analyst will use the new version once it is re-indexed." });
      onSaved();
    } catch (e) {
      setMessage({ tone: "danger", text: e instanceof ApiError && e.status === 409 ? "Someone else saved this document after you opened it. Copy your changes, reload, and apply them again." : e instanceof Error ? e.message : "Could not save this document." });
    } finally {
      setSaving(false);
    }
  }, [doc, saving, title, onSaved, switchEditing]);

  // ⌘S / Ctrl+S saves while editing.
  useEffect(() => {
    if (!editing) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") { e.preventDefault(); void save(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [editing, save]);

  if (doc.loading && !doc.data) return <LoadingState label="Loading document" />;
  if (doc.error || !doc.data) return <ErrorState message={doc.error || "Document not found"} onRetry={doc.reload} />;
  const d = doc.data;

  const cancel = () => {
    if (dirty && !window.confirm("Discard your unsaved changes?")) return;
    setDirty(false);
    switchEditing(false);
    setTitle(d.title);
    setDraftSource(null);
    setEditorKey((k) => k + 1); // remount from the saved text
    setMessage(null);
  };
  const copy = async (what: "link" | "text") => {
    await copyText(what === "link" ? `${window.location.origin}/knowledge/${d.doc_code}` : (editing ? editorRef.current?.getMarkdown() : null) || d.full_text || "");
    setCopied(what);
    window.setTimeout(() => setCopied(""), 1600);
  };

  return (
    <div className={historyOpen ? "kb-page with-history" : "kb-page"}>
      <div className="kb-doc">
        <div className={editing ? "kb-topbar editing" : "kb-topbar"}>
          {/* The code tells you which document you are in while scrolling. On phones, where the
              list is not beside the document, this is the way back to it. */}
          <a href="/knowledge" className="kb-back" onClick={(e) => { e.preventDefault(); onNavigate("/knowledge"); }}>
            <ChevronLeft aria-hidden="true" /><span>All documents</span>
          </a>
          <span className="kb-topbar-code">{d.doc_code}</span>
          <div className="kb-actions">
            {editing ? (
              <>
                <span className="kb-save-state" aria-live="polite">{saving ? "Saving…" : dirty ? "Unsaved changes" : "No changes"}</span>
                <button type="button" className="button secondary" onClick={cancel} disabled={saving}>Cancel</button>
                <button type="button" className="button" onClick={() => void save()} disabled={saving || (!dirty && title.trim() === d.title)}>
                  <Check aria-hidden="true" /> Save
                </button>
              </>
            ) : (
              <>
                <button type="button" className="kb-ghost" onClick={() => void copy("link")} title="Copy link">
                  {copied === "link" ? <Check aria-hidden="true" /> : <Link2 aria-hidden="true" />}<span>{copied === "link" ? "Copied" : "Copy link"}</span>
                </button>
                <button type="button" className="kb-ghost" onClick={() => void copy("text")} title="Copy document text">
                  {copied === "text" ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}<span>{copied === "text" ? "Copied" : "Copy text"}</span>
                </button>
                {canEdit && (
                  <button type="button" className="kb-ghost" onClick={() => setHistoryOpen((v) => !v)} aria-pressed={historyOpen} title="Version history">
                    <History aria-hidden="true" /><span>History</span>
                  </button>
                )}
                {canEdit && (
                  <button type="button" className="button" onClick={() => { switchEditing(true); setMessage(null); window.setTimeout(() => editorRef.current?.focus(), 0); }}>
                    <Pencil aria-hidden="true" /> Edit
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <article className="kb-article" ref={article}>
          {editing ? (
            <textarea
              className="kb-title-input"
              rows={1}
              value={title}
              ref={(el) => { if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; } }}
              onChange={(e) => { setTitle(e.target.value.replace(/\n/g, " ")); setDirty(true); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); editorRef.current?.focus(); } }}
              aria-label="Document title"
              maxLength={255}
            />
          ) : (
            <h1 className="kb-title">{d.title}</h1>
          )}
          <p className="kb-meta">
            <span className="kb-code">{d.doc_code}</span>
            <span>Version {d.version}</span>
            <span>{audience(d.applies_to)}</span>
            <span>Updated {formatDate(d.updated_at)}</span>
          </p>
          {message && <p role={message.tone === "danger" ? "alert" : "status"} className={`inline-notice ${message.tone}`}>{message.text}</p>}
          {!d.full_text?.trim() && !editing && draftSource === null ? (
            <div className="kb-empty">
              <p>This document has no text yet.</p>
              {canEdit && <button type="button" className="button secondary" onClick={() => setEditing(true)}><Pencil aria-hidden="true" /> Start writing</button>}
            </div>
          ) : (
            editing ? (
              // The editor shows the document the way the reader does (rebuilt tables, headings in title
              // case, the cover lines in the cover's type), so nothing restyles itself on Edit; a save
              // stores that tidier text.
              <DocEditor key={`${editorKey}-edit`} ref={editorRef} markdown={editableMarkdown(draftSource ?? d.full_text ?? "")} editable title={d.title} onDirty={markDirty} />
            ) : (
              // Reading view: the cover lines are pulled into a cover block above the body.
              <ReadingDoc key={`${editorKey}-read`} source={draftSource ?? d.full_text ?? ""} title={d.title} editorRef={editorRef} onDirty={markDirty} />
            )
          )}
        </article>
      </div>

      {historyOpen && canEdit && (
        <KbHistory
          code={d.doc_code}
          onClose={() => setHistoryOpen(false)}
          onRestore={(text) => {
            setDraftSource(text);
            setEditorKey((k) => k + 1);
            setEditing(true);
            setDirty(true);
            setMessage({ tone: "success", text: "An earlier version is loaded into the editor. Review it, then Save to make it current." });
          }}
        />
      )}
    </div>
  );
}

/** A Word paste as structured Markdown with calm headings: what both the reader and the editor show. */
const editableMarkdown = (source: string) => calmHeadings(plainTextToMarkdown(source));

function ReadingDoc({ source, title, editorRef, onDirty }: { source: string; title: string; editorRef: RefObject<DocEditorHandle | null>; onDirty: () => void }) {
  const { cover, body } = useMemo(() => splitCover(editableMarkdown(source), title), [source, title]);
  return (
    <>
      {cover && (
        <div className="kb-cover">
          {cover.eyebrow && <p className="kb-cover-eyebrow">{cover.eyebrow}</p>}
          {cover.title && <p className="kb-cover-title">{cover.title}</p>}
          {cover.lines.map((line, i) => <p key={i} className="kb-cover-line">{line}</p>)}
          {cover.meta && <p className="kb-cover-meta">{cover.meta}</p>}
        </div>
      )}
      <DocEditor ref={editorRef} markdown={body} editable={false} onDirty={onDirty} />
    </>
  );
}

function KbHistory({ code, onClose, onRestore }: { code: string; onClose: () => void; onRestore: (text: string) => void }) {
  const history = useApi<KbVersion[]>(`/api/kb/${encodeURIComponent(code)}/history`);
  return (
    <aside className="kb-history" aria-label="Version history">
      <div className="kb-history-head">
        <strong>Version history</strong>
        <button type="button" className="icon-button" aria-label="Close history" onClick={onClose}><X aria-hidden="true" /></button>
      </div>
      <p className="kb-history-note">Each save keeps the text it replaced. Restoring loads it into the editor; nothing changes until you save.</p>
      {history.loading ? <LoadingState label="Loading history" /> : history.error ? <ErrorState message={history.error} onRetry={history.reload} /> : !history.data?.length ? (
        <p className="kb-history-empty">No earlier versions yet. They appear here after the first edit made in this app.</p>
      ) : (
        <ol className="kb-history-list">
          {history.data.map((v) => (
            <li key={v.id}>
              <div><strong>{formatDateTime(v.created_at)}</strong>{v.edited_by && <span>Replaced by {v.edited_by}</span>}</div>
              <p>{(v.full_text || "(empty)").replace(/\s+/g, " ").slice(0, 160)}</p>
              {v.full_text !== null && (
                <button type="button" className="kb-ghost" onClick={() => onRestore(v.full_text || "")}><RotateCcw aria-hidden="true" /><span>Restore this version</span></button>
              )}
            </li>
          ))}
        </ol>
      )}
    </aside>
  );
}
